import { useForm } from 'react-hook-form';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AuthService from '../../../services/auth.service';
import { Alert } from '../../../services/notiflix.service';
import SocialMediaButton from '../../../components/common/auth/SocialMediaButton';
import SignupSecondStep from './SignupSecondStep';
import AuthCommonButton from '../../../components/common/auth/AuthCommonButton';
import AuthRootComponent from '../../../components/common/auth/AuthRootComponent';
import AuthHeaderTextComponent from '../../../components/common/auth/AuthHeaderTextComponent';
import TurnstileWidget from '../../../components/common/auth/TurnstileWidget';
import { useTranslation } from 'react-i18next';
import i18n from '../../../utils/i18n';
import { useSelector } from 'react-redux';
import configService from '../../../services/config.service';
const Signup = () => {
  const [isSignupComplete, setSignupComplete] = useState(false);
  const [isFirstName, setFirstName] = useState(null);
  const [isLoader, setLoader] = useState(false);
  const [emailCheckError, setEmailCheckError] = useState({
    show: false,
    message: '',
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileEnabled = !!configService.turnstileSiteKey();
  const { t } = useTranslation(['profile', 'common', 'auth']);
  const language = useSelector((state) => state.languageReducer.language);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language]);
  const schema = Yup.object({
    email: Yup.string()
      .required(t('auth:email_required'))
      .email(t('auth:email_invalid')),
    full_name: Yup.string()
      .required(t('auth:full_name_required'))
      .transform((value) => (value ? value.trim() : ''))
      .matches(
        /^[\p{L}\p{M}\p{N}\p{P}\p{S}]+(?:\s+[\p{L}\p{M}\p{N}\p{P}\p{S}]+)+$/u,
        t('auth:name_format_required'),
      )
      .max(40),
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSignup = async (_input) => {
    try {
      if (turnstileEnabled && !turnstileToken) {
        Alert.error(t('auth:captcha_required'));
        return false;
      }
      setLoader(true);

      const signupData = {
        email: _input.email,
        full_name: _input.full_name,
        lang: language,
        'cf-turnstile-response': turnstileToken,
      };

      let response = await AuthService.register(signupData);
      if (response.status === 'failure') {
        setLoader(false);
        setTurnstileToken(null);
        turnstileRef.current?.reset();
        Alert.error(response.message);
        return false;
      }
      let firstName = _input?.full_name ? _input.full_name.split(' ') : null;
      setFirstName(firstName[0]);
      setLoader(false);
      setSignupComplete(true);
    } catch (error) {
      setLoader(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      Alert.error(error.message);
      return false;
    }
  };
  const emailCheck = async (_input) => {
    if (_input.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
      try {
        const formData = {
          type: 'email',
          value: _input,
        };
        let _response = await AuthService.checkUserExists(formData);

        if (_response.status === 'failure') {
          setEmailCheckError({
            show: true,
            message: _response.message,
          });

          return false;
        } else {
          setEmailCheckError({
            show: false,
            message: '',
          });
        }
      } catch (error) {
        setEmailCheckError({
          show: true,
          message: error.message,
        });
        return false;
      }
      return false;
    }
    setEmailCheckError(false);
    return false;
  };
  return (
    <>
      <AuthRootComponent>
        <div className="w-full md:w-96 bg-white dark:bg-slate-800 dark:text-slate-200 px-6 py-10 rounded-md m-6">
          {!isSignupComplete && (
            <div className="w-full ">
              <AuthHeaderTextComponent
                title={t('common:welcome_message', {
                  appName: configService.appName(),
                })}
                desc={t('auth:create_account', {
                  appName: configService.appName(),
                })}
              />
              <form onSubmit={handleSubmit(onSignup)}>
                <input
                  placeholder={t('full_name')}
                  name="full_name"
                  type="text"
                  {...register('full_name', { required: true })}
                  className="mb-2 mt-2"
                />
                <div className="common-error-text">
                  {errors.full_name?.message}
                </div>
                <input
                  placeholder={t('common:email_address')}
                  name="email"
                  type="email"
                  onInput={(e) => emailCheck(e.target.value)}
                  {...register('email', { required: true })}
                  className="mb-2 mt-6"
                />
                {emailCheckError.show === false && (
                  <div className="common-error-text">
                    {errors.email?.message}
                  </div>
                )}
                {emailCheckError.show && (
                  <div className="common-error-text">
                    {emailCheckError?.message || ''}
                  </div>
                )}
                <TurnstileWidget
                  ref={turnstileRef}
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                />
                <div className="mt-5">
                  <AuthCommonButton
                    isLoader={isLoader}
                    Title={'Continue'}
                    isDisabled={
                      emailCheckError.show ||
                      (turnstileEnabled && !turnstileToken)
                    }
                  />
                </div>
              </form>
              <div className="text-[#85919F] text-center mt-4 text-sm">
                {t('auth:already_have_account')}
              </div>

              <div className="my-2 text-center">
                <Link
                  to="/login"
                  className="text-[#3D4CB2] dark:text-GreenColor text-center inline-block text-sm"
                >
                  Login
                </Link>
              </div>
              <SocialMediaButton />
            </div>
          )}
          {isSignupComplete && <SignupSecondStep firstName={isFirstName} />}
        </div>
      </AuthRootComponent>
    </>
  );
};

export default Signup;
